<template>
  <div class="sub-dash">

    <!-- ── HEADER ── -->
    <div class="sub-dash__header">
      <div>
        <h2 class="sub-dash__title">Subscriptions</h2>
        <p class="sub-dash__subtitle text-muted">Overview &amp; quick actions</p>
      </div>
      <div class="d-flex gap-2 align-items-center flex-wrap">
        <!-- Period filter (shown when user can view wallet balance) -->
        <template v-if="can('subscriptions.view_wallet_balance')">
          <div class="sub-dash__period d-flex align-items-center gap-1">
            <date-range-picker :auto-apply="true" :ranges="false" :append-to-body="true" v-model="periodPicker" class="sub-dash__date-input">
              <template #input="picker">
                <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} – {{ formatPickerDate(picker.endDate) }}</span>
                <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>Select period</span>
              </template>
            </date-range-picker>
            <button
              v-if="periodStart || periodEnd"
              class="btn btn-sm btn-link text-muted p-0"
              title="Clear period"
              @click="clearPeriod"
            >
              <i class="bi bi-x-circle"></i>
            </button>
          </div>
        </template>
        <button
          v-if="can('subscriptions.checkin')"
          class="btn btn-sm btn-outline-primary"
          @click="$refs.attendanceModal.show()"
        >
          <i class="bi bi-person-check mr-1"></i>Attendance
        </button>
        <button
          v-if="can('subscriptions.create')"
          class="btn btn-sm btn-primary"
          @click="$refs.createModal.show()"
        >
          <i class="bi bi-plus mr-1"></i>New Subscription
        </button>
        <div class="dropdown">
          <button
            class="btn btn-sm btn-outline-secondary dropdown-toggle"
            data-toggle="dropdown"
          >More</button>
          <div class="dropdown-menu dropdown-menu-right">
            <a
              v-if="can('subscriptions.manage_wallet')"
              class="dropdown-item"
              href="#"
              @click.prevent="$refs.walletModal.show()"
            >
              <i class="bi bi-wallet mr-2"></i>Load Wallet
            </a>
            <a
              v-if="can('subscriptions.view_financials')"
              class="dropdown-item"
              href="#"
              @click.prevent="$refs.statementsModal.show()"
            >
              <i class="bi bi-file-earmark-text mr-2"></i>Statements
            </a>
            <a
              v-if="can('subscriptions.manage_qrcodes')"
              class="dropdown-item"
              href="#"
              @click.prevent="$refs.qrcodeManagerModal.show()"
            >
              <i class="bi bi-qr-code-scan mr-2"></i>QR Manager
            </a>
            <a
              class="dropdown-item"
              href="/subscription-packages"
            >
              <i class="bi bi-box-seam mr-2"></i>Packages
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- ── LOADING ── -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner /><p class="mt-2 text-muted">Loading dashboard…</p>
    </div>

    <template v-else>

      <!-- ── STATUS COUNTS ── -->
      <b-row class="mb-4">
        <b-col cols="6" md="3" class="mb-3">
          <div class="sub-stat-card sub-stat-card--active" @click="goToList('active')">
            <div class="sub-stat-card__val">{{ counts.active }}</div>
            <div class="sub-stat-card__lbl">Active</div>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="mb-3">
          <div class="sub-stat-card sub-stat-card--expired" @click="goToList('expired')">
            <div class="sub-stat-card__val">{{ counts.expired }}</div>
            <div class="sub-stat-card__lbl">Expired</div>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="mb-3">
          <div class="sub-stat-card sub-stat-card--due" @click="goToList('past_due')">
            <div class="sub-stat-card__val">{{ counts.past_due }}</div>
            <div class="sub-stat-card__lbl">Past Due</div>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="mb-3">
          <div class="sub-stat-card sub-stat-card--cancelled">
            <div class="sub-stat-card__val">{{ counts.cancelled }}</div>
            <div class="sub-stat-card__lbl">Cancelled</div>
          </div>
        </b-col>
      </b-row>

      <!-- ── WALLET BALANCE KPI ── -->
      <b-row
        v-if="can('subscriptions.view_wallet_balance') && walletBalance !== null"
        class="mb-4"
      >
        <b-col md="4" class="mb-3">
          <div class="sub-stat-card sub-stat-card--wallet">
            <div class="sub-stat-card__lbl mb-1">
              <i class="bi bi-wallet2 mr-1"></i>
              {{ periodStart && periodEnd ? 'Net Wallet Movement' : 'Total Wallet Balance' }}
            </div>
            <div class="sub-stat-card__val sub-stat-card__val--wallet">
              KES {{ fmt(walletBalance) }}
            </div>
            <div v-if="periodStart && periodEnd" class="sub-stat-card__period text-muted">
              {{ periodStart }} – {{ periodEnd }}
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- ── ATTENDANCE + FINANCIAL ROW ── -->
      <b-row class="mb-4">

        <!-- Attendance (always shown if user can check-in or view attendance) -->
        <b-col
          v-if="can('subscriptions.view_attendance') || can('subscriptions.checkin')"
          md="6"
          class="mb-3"
        >
          <b-card class="h-100">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <strong>Today's Attendance</strong>
              <button
                v-if="can('subscriptions.checkin')"
                class="btn btn-sm btn-outline-primary"
                @click="$refs.attendanceModal.show()"
              >
                Open Desk
              </button>
            </div>
            <b-row class="text-center">
              <b-col cols="4">
                <div class="sub-att-val sub-att-val--inside">{{ attendance.inside_now }}</div>
                <div class="sub-att-lbl">Inside Now</div>
              </b-col>
              <b-col cols="4">
                <div class="sub-att-val">{{ attendance.today_entries }}</div>
                <div class="sub-att-lbl">Entries Today</div>
              </b-col>
              <b-col cols="4">
                <div class="sub-att-val">{{ attendance.today_exits }}</div>
                <div class="sub-att-lbl">Exits Today</div>
              </b-col>
            </b-row>
            <hr class="my-2" />
            <div class="d-flex justify-content-around text-center small text-muted">
              <div><strong>{{ attendance.subscription }}</strong><br>Subscribers</div>
              <div><strong>{{ attendance.wallet }}</strong><br>Wallet</div>
              <div><strong>{{ attendance.manual_walkin || 0 }}</strong><br>Walk-ins</div>
            </div>
          </b-card>
        </b-col>

        <!-- Financials — only shown with view_financials permission -->
        <b-col
          v-if="can('subscriptions.view_financials') && financials"
          md="6"
          class="mb-3"
        >
          <b-card class="h-100">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <strong>Revenue</strong>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="$refs.statementsModal.show()"
              >
                Full Report
              </button>
            </div>
            <b-row class="text-center mb-3">
              <b-col cols="6">
                <div class="sub-rev-val">KES {{ fmt(financials.revenue_this_month) }}</div>
                <div class="sub-att-lbl">This Month</div>
              </b-col>
              <b-col cols="6">
                <div class="sub-rev-val text-muted">KES {{ fmt(financials.revenue_last_month) }}</div>
                <div class="sub-att-lbl">Last Month</div>
              </b-col>
            </b-row>
            <div
              v-if="financials.outstanding_balance > 0"
              class="alert alert-warning py-2 mb-2 d-flex justify-content-between align-items-center"
            >
              <span><i class="bi bi-exclamation-triangle mr-1"></i>Outstanding</span>
              <strong>KES {{ fmt(financials.outstanding_balance) }}</strong>
            </div>
            <!-- By-package breakdown -->
            <div v-if="financials.by_package && financials.by_package.length" class="mt-2">
              <p class="text-muted small mb-1">BY PACKAGE (THIS MONTH)</p>
              <div
                v-for="row in financials.by_package"
                :key="row.package"
                class="d-flex justify-content-between small border-bottom py-1"
              >
                <span>{{ row.package }}</span>
                <strong>KES {{ fmt(row.revenue) }}</strong>
              </div>
            </div>
          </b-card>
        </b-col>

      </b-row>

      <!-- ── EXPIRING SOON ── -->
      <b-card v-if="expiring" class="mb-4">
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
          <strong>Expiring Subscriptions</strong>
          <button class="btn btn-sm btn-outline-secondary" @click="goToList('active')">
            View All Active
          </button>
        </div>
        <b-row class="text-center">
          <b-col cols="6">
            <div class="sub-att-val sub-att-val--warn">{{ expiring.in_7_days }}</div>
            <div class="sub-att-lbl">Expiring in 7 days</div>
          </b-col>
          <b-col cols="6">
            <div class="sub-att-val">{{ expiring.in_30_days }}</div>
            <div class="sub-att-lbl">Expiring in 30 days</div>
          </b-col>
        </b-row>
      </b-card>

    </template>

    <!-- ── SUBSCRIPTION LIST (below the dashboard cards) ── -->
    <subscriptions-index ref="subIndex" :initial-status="listStatus" />

    <!-- ── MODALS (mounted here, shared) ── -->
    <create-subscription-modal ref="createModal" @created="onCreated" />
    <load-customer-wallet-modal v-if="can('subscriptions.manage_wallet')" ref="walletModal" />
    <attendance-dashboard-modal v-if="can('subscriptions.checkin')" ref="attendanceModal" />
    <subscription-statements-modal v-if="can('subscriptions.view_financials')" ref="statementsModal" />
    <subscription-qr-code-manager-modal v-if="can('subscriptions.manage_qrcodes')" ref="qrcodeManagerModal" />

  </div>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'
import SubscriptionsIndex from './SubscriptionsIndex.vue'
import LoadCustomerWalletModal from './modals/LoadCustomerWalletModal.vue'
import AttendanceDashboardModal from './modals/AttendanceDashboardModal.vue'
import SubscriptionStatementsModal from './modals/SubscriptionStatementsModal.vue'
import SubscriptionQrCodeManagerModal from './modals/SubscriptionQrCodeManagerModal.vue'
import CreateSubscriptionModal from './CreateSubscriptionModal.vue'
import subscriptionsAdminMixin from '../../mixins/subscriptionsAdminMixin.js'

export default {
  name: 'SubscriptionDashboard',
  mixins: [subscriptionsAdminMixin],

  components: {
    SubscriptionsIndex,
    LoadCustomerWalletModal,
    AttendanceDashboardModal,
    SubscriptionStatementsModal,
    SubscriptionQrCodeManagerModal,
    CreateSubscriptionModal,
    DateRangePicker,
  },

  data() {
    return {
      loading:       false,
      counts:        { active: 0, expired: 0, past_due: 0, cancelled: 0 },
      attendance:    { inside_now: 0, today_entries: 0, today_exits: 0, subscription: 0, wallet: 0, manual_walkin: 0 },
      expiring:      null,
      financials:    null,
      walletBalance: null,
      listStatus:    null,
      periodStart:   '',
      periodEnd:     '',
    }
  },

  mounted() {
    this.loadDashboard()
  },

  computed: {
    periodPicker: {
      get() {
        return {
          startDate: this.periodStart ? new Date(this.periodStart) : null,
          endDate: this.periodEnd ? new Date(this.periodEnd) : null,
        };
      },
      set(val) {
        this.periodStart = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '';
        this.periodEnd = val && val.endDate ? moment(val.endDate).format('YYYY-MM-DD') : '';
        this.loadDashboard();
      },
    },
  },

  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    async loadDashboard() {
      this.loading = true
      try {
        const params = {}
        if (this.periodStart) params.start_date = this.periodStart
        if (this.periodEnd)   params.end_date   = this.periodEnd

        const { data } = await axios.get('/api/subscriptions/dashboard', { params })
        this.counts        = data.counts        || this.counts
        this.attendance    = data.attendance    || this.attendance
        this.expiring      = data.expiring_soon || null
        this.financials    = data.financials    || null
        this.walletBalance = data.total_wallet_balance !== undefined
          ? data.total_wallet_balance
          : null
      } catch (e) {
        this.$bvToast.toast('Failed to load dashboard.', { title: 'Error', variant: 'danger' })
      } finally {
        this.loading = false
      }
    },

    clearPeriod() {
      this.periodStart = ''
      this.periodEnd   = ''
      this.loadDashboard()
    },

    onCreated() {
      this.loadDashboard()
      this.$refs.subIndex && this.$refs.subIndex.loadSubscriptions()
    },

    goToList(status) {
      this.listStatus = status
    },

    fmt(val) {
      return parseFloat(val || 0).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
}
</script>

<style scoped>
.sub-dash { font-size: 14px; }

.sub-dash__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.sub-dash__title { font-size: 22px; font-weight: 700; margin: 0; color: var(--bs-primary); }
.sub-dash__subtitle { font-size: 13px; margin: 2px 0 0; }

/* Status cards */
.sub-stat-card {
  border: 1px solid var(--bs-light);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  background: #fff;
  cursor: pointer;
  transition: box-shadow .15s;
}
.sub-stat-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,.08); }
.sub-stat-card__val { font-size: 32px; font-weight: 700; line-height: 1; margin-bottom: 4px; }
.sub-stat-card__lbl { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: var(--bs-secondary); }

.sub-stat-card--active  .sub-stat-card__val { color: #198754; }
.sub-stat-card--expired .sub-stat-card__val { color: #6c757d; }
.sub-stat-card--due     .sub-stat-card__val { color: #dc3545; }
.sub-stat-card--cancelled .sub-stat-card__val { color: #adb5bd; }

/* Attendance values */
.sub-att-val { font-size: 26px; font-weight: 700; }
.sub-att-val--inside { color: #0d6efd; }
.sub-att-val--warn   { color: #fd7e14; }
.sub-att-lbl { font-size: 11px; text-transform: uppercase; letter-spacing: .8px; color: var(--bs-secondary); margin-top: 2px; }

/* Revenue values */
.sub-rev-val { font-size: 20px; font-weight: 700; word-break: break-word; }

/* Wallet balance card */
.sub-stat-card--wallet { cursor: default; }
.sub-stat-card--wallet .sub-stat-card__lbl { font-size: 12px; color: #6c757d; }
.sub-stat-card__val--wallet { font-size: 28px; font-weight: 700; color: #0d6efd; }
.sub-stat-card__period { font-size: 11px; margin-top: 4px; }

/* Period filter */
.sub-dash__period { gap: 4px; }
.sub-dash__date-input { width: 130px; }
</style>
